import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Grid, CircularProgress, Alert, Button } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { deleteReferralRecordByPatientId } from '../../features/lma/referralsSlice';
import { LeadManagementProps } from './LeadManagement.types';
import CustomTable from 'components/LMA/Table';
import { StyledContainer, StyledPaper } from 'components/LMA/LMA.style';
import AlertMessage from '../LMA/AlertMessage';
import PaginationControls from 'components/LMA/PaginationControls';
import { ActiveFilters, ActiveSorts } from 'components/LMA/ActiveFilters';
import MergeComparisonModal from 'components/LMA/MergeComparisonModal';
import DynamicFilterPanel from 'components/LMA/DynamicFilterPanel';
import { DeduplicationMatch } from 'components/LMA/Table/DeduplicationTable';
import { ReferralData } from 'components/LMA/types';
import { useAppData, useReferralData, useMergeModal } from 'components/LMA/hooks';
import Header from 'components/LMA/Header/Header';
import { useCurrentTab } from 'hooks/useCurrentTab';
import { getTabConfigurationByIndex } from './utils/configBuilder';
import { buildPageConfig, getExpandedViewConfig } from './utils/configAdapter';
import { FilterButton, TabBar } from 'components/LMA';
import {
  TAB_KEYS,
  normalizeTabKey,
  QUICK_FILTER_KEYS,
  LOCKED_BY_ME_NO_LOCKS_LEAD_IDS,
} from './constants';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import {
  addLMAUser,
  getLeadLocks,
  getLockedLeadIdsByUser,
  removeLeadLock,
  setLockStorageType,
  setLockTimeoutSeconds,
} from 'src/services/leadManagementApi';
import { logger } from '../../logger';
import { useRouter } from 'next/router';
import ToastNotification from '../shared/ToastNotification/ToastNotification';
import ConfirmationDialog from 'components/LMA/Table/ConfirmationDialog';
import { useAuth, LMA_USERNAME_LOCAL_KEY } from 'contexts/AuthContext';
import { deleteNonReferralRecordByPatientId } from 'src/features/lma/nonReferralsSlice';
import { deleteFlaggedRecordByPatientId } from 'src/features/lma/flaggedRecordsSlice';

const CURRENT_USER = 'Current User';

const LeadManagement = (props: LeadManagementProps): JSX.Element => {
  const baseTheme = useTheme();
  const router = useRouter();
  const { userRole, userName: authUserName } = useAuth();
  // userRole is resolved to 'LMASuperAdmin' or 'AvacareLeadManager' by AuthContext
  const isSuperAdmin = userRole === 'LMASuperAdmin';
  // Lock config from Sitecore (LMASiteSettings → _LMA Lock Settings)
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const enableLocking = siteSettings?.EnableLocking?.value ?? false;
  const phoneRegex = siteSettings?.PhoneRegex?.value || '';
  const lockStorageType = (siteSettings?.LockStorageType?.value as 'Redis' | 'SQL') || 'Redis';
  const maxLockedRecords = siteSettings?.MaxLockedRecords?.value || 1;
  const lockTimeoutSeconds = siteSettings?.LockTimeoutSeconds?.value || 3600;
  const dropdownLoadedTabsRef = useRef<Set<string>>(new Set());

  // Both env flag AND Sitecore flag must be true
  const isLockEnabled = process.env.NEXT_PUBLIC_LMA_LOCK_ENABLED === 'true' && enableLocking;

  // Set storage type and lock timeout — all lock functions in leadManagementApi.ts auto-route
  useEffect(() => {
    setLockStorageType(lockStorageType);
    setLockTimeoutSeconds(lockTimeoutSeconds);
  }, [lockStorageType, lockTimeoutSeconds]);

  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [slowLoadMessage, setSlowLoadMessage] = useState(false);

  // Prefer AuthContext userName (set when JWT is ready); localStorage can lag on first paint after ?token= login
  const username =
    authUserName?.trim() ||
    (typeof window !== 'undefined' ? localStorage.getItem(LMA_USERNAME_LOCAL_KEY) : null) ||
    '';
  const existingUserId = localStorage.getItem('lma-userId');
  useEffect(() => {
    if (!username || existingUserId) return;
    const addUser = async () => {
      try {
        logger.info(`Adding user to LMAUsers table`, {
          response: username,
          payload: {},
        });
        const userId = await addLMAUser(username);
        if (userId) {
          localStorage.setItem('lma-userId', userId);
        }
      } catch (error: any) {
        logger.error(`An error occurred while adding the user`, {
          error: error?.message,
          payload: {},
        });
        dispatch({
          type: 'toast/showToast',
          payload: {
            message: error?.response?.data?.message || 'Failed to add user to LMA',
            severity: 'error',
          },
        });
      }
    };
    addUser();
  }, [username, existingUserId, dispatch]);

  // Build dynamic tabs from Sitecore props
  const dynamicTabs = useMemo(() => {
    const tabs = [];

    if (props.fields.Referral) {
      const sitecoreTabKey = props.fields.Referral.fields.TabKey?.value || '';
      tabs.push({
        label: props.fields.Referral.fields.TabName?.value || 'Referrals',
        value: 0,
        dataKey: normalizeTabKey(sitecoreTabKey) || TAB_KEYS.REFERRALS,
      });
    }

    if (props.fields.NonReferral) {
      const sitecoreTabKey = props.fields.NonReferral.fields.TabKey?.value || '';
      tabs.push({
        label: props.fields.NonReferral.fields.TabName?.value || 'Non-Referrals',
        value: 1,
        dataKey: normalizeTabKey(sitecoreTabKey) || TAB_KEYS.NON_REFERRALS,
      });
    }

    if (props.fields.ProcessedToCC) {
      const sitecoreTabKey = props.fields.ProcessedToCC.fields.TabKey?.value || '';
      tabs.push({
        label: props.fields.ProcessedToCC.fields.TabName?.value || 'Processed to CC',
        value: 2,
        dataKey: normalizeTabKey(sitecoreTabKey) || TAB_KEYS.PROCESSED_REFERRALS,
      });
    }

    if (props.fields.FlaggedRecords) {
      const sitecoreTabKey = props.fields.FlaggedRecords.fields.TabKey?.value || '';
      tabs.push({
        label: props.fields.FlaggedRecords.fields.TabName?.value || 'Flagged Records',
        value: 3,
        dataKey: normalizeTabKey(sitecoreTabKey) || TAB_KEYS.FLAGGED_RECORDS,
      });
    }

    return tabs;
  }, [props.fields]);

  const currentTabKey = useMemo(() => {
    const currentTab = dynamicTabs.find((tab) => tab.value === tabValue);
    return currentTab?.dataKey || '';
  }, [tabValue, dynamicTabs]);

  const {
    data,
    totalRecords,
    loading,
    error,
    currentPage,
    pageSize,
    filters,
    sortBy,
    sortOrder,
    applyFilters,
    changePage,
    changePageSize,
    changeSorting,
    clearSorting,
    loadDropdownData,
    clearFilters,
    loadData,
    processedDataView,
  } = useCurrentTab({
    currentTabKey,
    referralStatusFilters: props.fields.Referral?.fields?.LeadStatusFilters,
    nonReferralStatusFilters: props.fields.NonReferral?.fields?.LeadStatusFilters,
    processedStatusFilters: props.fields.ProcessedToCC?.fields?.LeadStatusFilters,
    flaggedRecordsStatusFilters: props.fields.FlaggedRecords?.fields?.LeadStatusFilters,
    defaultProcessedViewIsProfile: props.fields.DefaultProcessedViewIsProfile?.value === true,
  });

  // Get total records from all tabs for tab counts
  const referralsTotal = useSelector((state: RootState) => state.lmaReferrals.totalRecords);
  const nonReferralsTotal = useSelector((state: RootState) => state.lmaNonReferrals.totalRecords);
  const processedTotal = useSelector(
    (state: RootState) => state.lmaProcessedReferrals.totalRecords
  );
  const flaggedRecordsTotal = useSelector(
    (state: RootState) => state.lmaFlaggedRecords.totalRecords
  );

  const allTabTotals = useMemo(
    () => ({
      [TAB_KEYS.REFERRALS]: referralsTotal,
      [TAB_KEYS.NON_REFERRALS]: nonReferralsTotal,
      [TAB_KEYS.PROCESSED_REFERRALS]: processedTotal,
      [TAB_KEYS.FLAGGED_RECORDS]: flaggedRecordsTotal,
    }),
    [referralsTotal, nonReferralsTotal, processedTotal, flaggedRecordsTotal]
  );

  useEffect(() => {
    if (loading) {
      const slowLoadTimeout = Number(process.env.LMA_API_SLOW_LOAD_TIMEOUT_MS) || 7000;
      const slowTimer = setTimeout(() => {
        setSlowLoadMessage(true);
      }, slowLoadTimeout);
      return () => clearTimeout(slowTimer);
    }
    setSlowLoadMessage(false);
    return undefined;
  }, [loading]);

  useEffect(() => {
    loadData().then(() => {
      if (!dropdownLoadedTabsRef.current.has(currentTabKey)) {
        dropdownLoadedTabsRef.current.add(currentTabKey);
        loadDropdownData();
      }
      // Always refresh dropdown data when switching to Flagged Records tab
      // to ensure filter values reflect current state
      if (currentTabKey === TAB_KEYS.FLAGGED_RECORDS) {
        loadDropdownData();
      }
    });
    clearFilters();
  }, [currentTabKey]);

  // Build current tab configuration from Sitecore props with data for dynamic options
  const currentTabConfig = useMemo(() => {
    const isProcessed = currentTabKey === TAB_KEYS.PROCESSED_REFERRALS;
    // When viewing the Processed tab in full-profile mode, use PatientProfile fields for column/expanded config
    const processedTabItem =
      isProcessed && processedDataView?.useFullProfile && props.fields.PatientProfile
        ? props.fields.PatientProfile
        : props.fields.ProcessedToCC;

    return getTabConfigurationByIndex(
      tabValue,
      props.fields.Referral,
      props.fields.NonReferral,
      processedTabItem,
      props.fields.FlaggedRecords
    );
  }, [tabValue, props.fields, data, currentTabKey, processedDataView?.useFullProfile]);

  // Build PageConfig from currentTabConfig for table and filters
  const sitecoreConfig = useMemo(() => {
    return buildPageConfig(currentTabConfig);
  }, [currentTabConfig]);

  // Get expanded view configuration
  const expandedConfig = useMemo(() => {
    return getExpandedViewConfig(currentTabConfig, {
      personalInfoHeaderText: props.fields.PersonalInfoHeaderText?.value,
      demographicsHeaderText: props.fields.DemographicsHeaderText?.value,
      caregiverInfoHeaderText: props.fields.CaregiverInfoHeaderText?.value,
      trackingParamsHeaderText: props.fields.TrackingParamsHeaderText?.value,
      dedupAddParamsHeaderText: props.fields.DedupAddParamsHeaderText?.value,
      commentsHeaderText: props.fields.CommentsHeaderText?.value,
      resetFilterText: props.fields.ResetFilterText?.value,
    });
  }, [currentTabConfig, props.fields]);

  // Load configuration and data from CustomApp (legacy - keeping for updateTabData and updateRecordInAllTabs)
  const { currentTabData, updateTabData, updateRecordInAllTabs } = useAppData(tabValue);

  // Use actual username for lock attribution; fallback for display when not logged in
  const currentUser = username || CURRENT_USER;

  // Expand row only after successful lock (not on unlock or error)
  const [expandRowIdAfterLock, setExpandRowIdAfterLock] = useState<string | null>(null);
  const paginatedDataRef = useRef<ReferralData[]>([]);

  // Local filter state for UI updates before applying
  const [localFilters, setLocalFilters] = useState<Record<string, string | string[]>>({});

  // No client-side cache for locked IDs — always fetch fresh from the server so that
  // externally cleared locks (e.g. by admin) are reflected immediately without a page refresh.

  // Ref to prevent duplicate API calls
  const applyingFiltersRef = useRef(false);

  // Apply filters - triggers API call with complete filter object
  const handleApplyFilters = useCallback(
    async (filtersToApply: Record<string, string | string[]>) => {
      if (applyingFiltersRef.current) {
        return;
      }

      applyingFiltersRef.current = true;

      let cleanedFilters = Object.entries(filtersToApply).reduce((acc, [key, value]) => {
        const isEmpty =
          value === null || value === '' || (Array.isArray(value) && value.length === 0);

        if (!isEmpty) {
          acc[key] = value;
        }

        return acc;
      }, {} as Record<string, string | string[]>);

      // When "Locked by me" is selected, get lead IDs locked by current user from Redis/SQL and pass as leadIds; otherwise pass null
      const quickFiltersValue = cleanedFilters.quickFilters;
      const isLockedByMe = Array.isArray(quickFiltersValue)
        ? quickFiltersValue.includes(QUICK_FILTER_KEYS.LOCKED_BY_ME)
        : String(quickFiltersValue ?? '')
            .split(',')
            .map((s) => s.trim())

            .includes(QUICK_FILTER_KEYS.LOCKED_BY_ME);
      if (isLockedByMe) {
        // Same identity as addLMAUser / display: AuthContext first, then localStorage fallback
        const lockUser = username.trim() || null;
        if (lockUser) {
          let lockedLeadIds: string[];
          try {
            lockedLeadIds = await getLockedLeadIdsByUser(lockUser);
          } catch {
            lockedLeadIds = [];
          }

          if (lockedLeadIds.length === 0) {
            // Keep "Locked by me" applied; use sentinel leadIds so API returns no rows (same as empty IN list)
            cleanedFilters = {
              ...cleanedFilters,
              leadIds: LOCKED_BY_ME_NO_LOCKS_LEAD_IDS,
            };
          } else {
            cleanedFilters = {
              ...cleanedFilters,
              leadIds: lockedLeadIds.join(','),
            };
          }
        }
      } else {
        cleanedFilters = { ...cleanedFilters, leadIds: null as unknown as string };
      }

      // Phone filter: always derive both formats regardless of what the user typed
      // withoutFormattedPhoneNumber → digits only (e.g. "1234567890")
      // formattedPhoneNumber        → dashed 10-digit format (e.g. "123-456-7890")
      const rawPhone = cleanedFilters.phone as string | undefined;
      if (rawPhone) {
        const digitsOnly = rawPhone.replace(/\D/g, '');
        const formattedPhone =
          digitsOnly.length === 10
            ? `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`
            : digitsOnly;
        cleanedFilters = {
          ...cleanedFilters,
          withoutFormattedPhoneNumber: digitsOnly,
          formattedPhoneNumber: formattedPhone,
        };
      } else {
        delete cleanedFilters.withoutFormattedPhoneNumber;
        delete cleanedFilters.formattedPhoneNumber;
        cleanedFilters = cleanedFilters;
      }

      setLocalFilters(cleanedFilters);
      applyFilters(cleanedFilters);

      setTimeout(() => {
        applyingFiltersRef.current = false;
      }, 1000);
    },
    [applyFilters, username]
  );

  /** Remove "Locked by me" quick filter + derived leadIds; keep other filters. Used on manual unlock and after Send to CC / remove when lock is released. */
  const clearLockedByMeQuickFilter = useCallback(() => {
    const currentQuickFilters = Array.isArray(localFilters.quickFilters)
      ? localFilters.quickFilters
      : [];
    const hasLockedByMe = currentQuickFilters.includes(QUICK_FILTER_KEYS.LOCKED_BY_ME);
    if (!hasLockedByMe && !('leadIds' in localFilters)) return;

    const updatedQuickFilters = currentQuickFilters.filter(
      (f: string) => f !== QUICK_FILTER_KEYS.LOCKED_BY_ME
    );
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { leadIds, ...rest } = localFilters;
    const newFilters =
      updatedQuickFilters.length > 0
        ? { ...rest, quickFilters: updatedQuickFilters }
        : (() => {
            const { quickFilters: _qf, ...remaining } = rest; // eslint-disable-line @typescript-eslint/no-unused-vars
            return remaining;
          })();
    setLocalFilters(newFilters);
    applyFilters(newFilters);
  }, [localFilters, applyFilters]);

  // Manage referral data state
  const {
    data: referralData,
    setData: setReferralData,
    lockedRecordsCount,
    handleSelectRow,
    handleSelectAll: handleSelectAllData,
    handlePatientCCIdChange: handlePatientCCIdChangeData,
    handleStatusChange: handleStatusChangeData,
    handleLockToggle,
    lockLoadingLeadId,
    lockAlert,
    setLockAlert,
  } = useReferralData({
    currentUser,
    maxLockedRecords,
    onLockOrUnlock: useCallback((lockedLeadId?: string) => {
      if (lockedLeadId) {
        const row = paginatedDataRef.current.find(
          (r) => String(r.leadId ?? r.id) === String(lockedLeadId)
        );
        if (row?.patientId) setExpandRowIdAfterLock(row.patientId);
      }
    }, []),
    onAutoClearFiltersOnUnlock: clearLockedByMeQuickFilter,
  });

  // Sync Redux data with referralData when tab or pagination changes; fetch lock state for current page so locked rows show correctly
  useEffect(() => {
    if (data.length === 0) {
      setReferralData([]);
      return;
    }
    if (!isLockEnabled) {
      // Lock feature disabled — just use raw data without lock state
      setReferralData(data);
      return;
    }
    // Show new data immediately, carrying over any existing lock state to prevent flash
    setReferralData((prev) => {
      const prevLocks = new Map(
        prev.map((r) => [String(r.leadId ?? r.id), { lockedBy: r.lockedBy, lockedAt: r.lockedAt }])
      );
      return data.map((row) => {
        const existing = prevLocks.get(String(row.leadId ?? row.id));
        return existing?.lockedBy
          ? { ...row, lockedBy: existing.lockedBy, lockedAt: existing.lockedAt }
          : row;
      });
    });
    // Then refresh lock state from Redis
    const leadIds = data.map((r) => r.leadId ?? r.id);
    getLeadLocks(leadIds)
      .then((locks) => {
        const merged = data.map((row) => {
          const rowLeadId = row.leadId ?? row.id;
          const lockedBy = locks[rowLeadId] ?? null;
          return {
            ...row,
            lockedBy,
            lockedAt: lockedBy ? row.lockedAt ?? null : null,
          };
        });
        setReferralData(merged);
      })
      .catch(() => {
        // Already set to data above; no further action needed
      });
  }, [tabValue, data, setReferralData]);

  // Keep ref updated so onLockOrUnlock can find row to expand after successful lock
  useEffect(() => {
    paginatedDataRef.current = referralData;
  }, [referralData]);

  // Sync referralData changes back to allData (debounced to prevent loops)
  useEffect(() => {
    if (sitecoreConfig && referralData.length > 0) {
      const dataKey = sitecoreConfig.tabs[tabValue]?.dataKey;
      if (dataKey && currentTabData.length > 0) {
        // Only update if there are actual changes to prevent circular updates
        const hasChanges = JSON.stringify(referralData) !== JSON.stringify(currentTabData);
        if (hasChanges) {
          updateTabData(dataKey, () => referralData);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralData]);

  // Manage merge modal
  const {
    mergeModalOpen,
    selectedMergeRow,
    selectedMergeTarget,
    handleOpenMergeModal: openMergeModal,
    handleCloseMergeModal,
    handleMerge: performMerge,
    handleRetryMerge,
    dialogMode,
    resultMessage,
    resultType,
    showDialog,
    setShowDialog,
    isSending,
  } = useMergeModal(currentUser, isLockEnabled, (message) =>
    setLockAlert({ severity: 'error', message })
  );

  // Calculate pagination values from server response
  const totalPages = useMemo(
    () => Math.ceil(totalRecords / pageSize) || 1,
    [totalRecords, pageSize]
  );

  // Use referralData so lock state (lockedBy) is visible; synced from Redux data on load
  const paginatedData = referralData;

  // Calculate active filter count
  // Exclude internal/derived keys that are not user-facing filters
  const activeFilterCount = useMemo(() => {
    const internalFilterKeys = ['leadIds', 'withoutFormattedPhoneNumber', 'formattedPhoneNumber']; // leadIds is derived from lockedByMe quick filter, not a user-facing filter
    let count = 0;
    let hasMatchPercentage = false;

    Object.entries(filters).forEach(([key, value]) => {
      if (internalFilterKeys.includes(key)) return; // skip internal filters
      if (value !== '' && value !== null && (!Array.isArray(value) || value.length > 0)) {
        if (key === 'matchPercentageMin' || key === 'matchPercentageMax') {
          hasMatchPercentage = true;
        } else {
          count++;
        }
      }
    });

    if (hasMatchPercentage) {
      count++;
    }

    return count;
  }, [filters]);

  // Sync local filters with Redux filters when tab changes
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters, tabValue]);

  // Handle filter removal from ActiveFilters - triggers immediate API call
  const handleRemoveFilter = useCallback(
    (field: string, value: string | string[]) => {
      setLocalFilters((prev) => {
        const updatedFilters = { ...prev, [field]: value };

        // When quickFilters changes (e.g. "lockedByMe" removed), also remove the derived leadIds filter
        if (field === 'quickFilters') {
          const qfArray = Array.isArray(value) ? value : [];
          if (!qfArray.includes(QUICK_FILTER_KEYS.LOCKED_BY_ME)) {
            delete updatedFilters.leadIds;
          }
        }

        // When phone filter is removed, also remove the derived phone keys
        if (field === 'phone') {
          const isEmpty =
            value === '' || value === null || (Array.isArray(value) && value.length === 0);
          if (isEmpty) {
            delete (updatedFilters as any).withoutFormattedPhoneNumber;
            delete (updatedFilters as any).formattedPhoneNumber;
          }
        }

        // Trigger API call with updated filters
        applyFilters(updatedFilters);
        return updatedFilters;
      });
    },
    [applyFilters]
  );

  const handleClearFiltersWithReset = useCallback(() => {
    setLocalFilters({});
    applyFilters({});
  }, [applyFilters]);

  // Selection state
  const allSelected = useMemo(
    () => paginatedData.length > 0 && paginatedData.every((item: ReferralData) => item.selected),
    [paginatedData]
  );
  const someSelected = useMemo(
    () => paginatedData.some((item: ReferralData) => item.selected),
    [paginatedData]
  );

  // Event handlers
  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setFilterOpen(false); // Close filter panel when tab changes
    setLocalFilters({});
  }, []);

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      const visibleIds = paginatedData.map((item: ReferralData) => item.id);
      handleSelectAllData(checked, visibleIds);
    },
    [paginatedData, handleSelectAllData]
  );

  const handlePageChange = useCallback(
    (_event: React.ChangeEvent<unknown>, value: number) => {
      changePage(value);
    },
    [changePage]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      changePageSize(newPageSize);
    },
    [changePageSize]
  );

  // Handle sorting changes from table column headers
  const handleSort = useCallback(
    (field: string) => {
      let newSortOrder: 'asc' | 'desc' = 'asc';

      if (sortBy === field) {
        newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      }

      changeSorting(field, newSortOrder);
    },
    [sortBy, sortOrder, changeSorting]
  );

  const handlePatientCCIdChange = useCallback(
    (id: string, newValue: string) => {
      handlePatientCCIdChangeData(id, newValue);
      updateRecordInAllTabs(id, (record) => ({ ...record, patientCCId: newValue }));
    },
    [handlePatientCCIdChangeData, updateRecordInAllTabs]
  );

  const handleStatusChange = useCallback(
    (id: string, newValue: string) => {
      handleStatusChangeData(id, newValue);
      updateRecordInAllTabs(id, (record) => ({ ...record, status: newValue }));
    },
    [handleStatusChangeData, updateRecordInAllTabs]
  );

  const handleOpenMergeModal = useCallback(
    (match: DeduplicationMatch | undefined, row: ReferralData) => {
      openMergeModal(match, row);
    },
    [openMergeModal]
  );

  const handleRemoveRecord = useCallback(
    (patientId: string) => {
      // Auto-unlock: find the record and remove its lock before removing from the list
      if (isLockEnabled) {
        const record = referralData.find((item) => item.patientId === patientId);
        if (record && record.lockedBy === currentUser) {
          const leadId = String(record.leadId ?? record.id);
          removeLeadLock(leadId, currentUser).catch((err) =>
            console.warn('[LeadManagement] Auto-unlock on remove failed:', err?.message)
          );
          clearLockedByMeQuickFilter();
        }
      }
      if (currentTabKey === TAB_KEYS.REFERRALS) {
        dispatch(deleteReferralRecordByPatientId(patientId));
      }
      if (currentTabKey === TAB_KEYS.NON_REFERRALS) {
        dispatch(deleteNonReferralRecordByPatientId(patientId));
      }
      if (currentTabKey === TAB_KEYS.FLAGGED_RECORDS) {
        dispatch(deleteFlaggedRecordByPatientId(patientId));
        // Refresh dropdown values after unflagging a record
        loadDropdownData();
      }
      updateTabData(currentTabKey, (currentData) =>
        currentData.filter((item) => item.patientId !== patientId)
      );
    },
    [
      currentTabKey,
      updateTabData,
      dispatch,
      isLockEnabled,
      referralData,
      currentUser,
      clearLockedByMeQuickFilter,
      loadDropdownData,
    ]
  );

  const handleCancelAction = () => {
    setShowDialog(false);
  };

  const handleCloseDialog = () => {
    if (dialogMode === 'result' && resultType === 'success' && selectedMergeRow?.patientId) {
      handleRemoveRecord(selectedMergeRow.patientId);
      // Close the merge modal after successful merge
      handleCloseMergeModal();
    }
    setShowDialog(false);
  };

  const isProcessedTab = currentTabKey === TAB_KEYS.PROCESSED_REFERRALS;
  const hideTabSpecificFeatures =
    currentTabKey === TAB_KEYS.PROCESSED_REFERRALS || currentTabKey === TAB_KEYS.FLAGGED_RECORDS;

  const processedViewToggle =
    isProcessedTab && processedDataView && props?.fields?.EnableProcessedViewSwitch?.value ? (
      <Button
        variant="outlined"
        size="small"
        disabled={loading}
        onClick={() => void processedDataView.switchView(!processedDataView.useFullProfile)}
        sx={{
          textTransform: 'none',
          borderColor: (theme: any) => theme.palette.themeColor?.main,
          color: (theme: any) => theme.palette.themeColor?.main,
          '&:hover': {
            borderColor: (theme: any) => theme.palette.themeColor?.main,
            backgroundColor: (theme: any) => theme.palette.themeLightColor?.main,
          },
        }}
      >
        {processedDataView.useFullProfile
          ? props.fields?.SwitchtoEnrollmentViewHeadingText?.value || 'Switch to Enrollment View'
          : props.fields?.SwitchtoProfileViewHeadingText?.value || 'Switch to Profile View'}
      </Button>
    ) : undefined;

  if (!sitecoreConfig) {
    return (
      <StyledContainer maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </StyledContainer>
    );
  }

  const headerFields = {
    HeaderTitle: props.fields.HeaderTitle,
    HeaderSummary: props.fields.HeaderSummary,
    LogoutLink: props.fields.LogoutLink,
  };

  return (
    <ThemeProvider theme={baseTheme}>
      <Header fields={headerFields} />
      <StyledContainer maxWidth={false}>
        <StyledPaper elevation={0}>
          <TabBar
            tabs={dynamicTabs}
            activeTab={tabValue}
            onTabChange={handleTabChange}
            allTabTotals={allTabTotals}
            disabled={loading}
            guidanceButtonText={props.fields.GuidanceButtonText}
            guidanceTitle={props.fields.GuidanceTitle}
            guidanceBody={props.fields.GuidanceBody}
            trailingActions={processedViewToggle}
          />

          <AlertMessage messageText={props.fields.AlertMessageText} />

          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          )}

          {slowLoadMessage && loading && (
            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
              We’re experiencing a delay. Thank you for your patience. Contact IT support if this
              persists.
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1.5,
                    flexWrap: 'wrap',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flex: 1 }}>
                    {sitecoreConfig.enableFilters && (
                      <Box sx={{ position: 'relative' }}>
                        <FilterButton
                          filterOpen={filterOpen}
                          onToggleFilter={() => setFilterOpen(!filterOpen)}
                          activeFilterCount={activeFilterCount}
                          label={props.fields.FilterHeading}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>

                <DynamicFilterPanel
                  filterOpen={filterOpen}
                  filters={localFilters}
                  onClearFilters={handleClearFiltersWithReset}
                  filterConfig={sitecoreConfig.filterConfig}
                  currentTabIndex={tabValue}
                  currentTabKey={currentTabKey}
                  onApplyFilters={handleApplyFilters}
                  phoneRegex={phoneRegex}
                />

                <ActiveFilters
                  filters={filters}
                  onFilterChange={handleRemoveFilter}
                  totalRecords={totalRecords}
                  filteredRecords={totalRecords}
                  filterConfig={sitecoreConfig.filterConfig}
                />

                <ActiveSorts
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onClearSort={clearSorting}
                  columns={sitecoreConfig.columns}
                />

                <CustomTable
                  config={sitecoreConfig}
                  someSelected={someSelected}
                  allSelected={allSelected}
                  handleSelectAll={handleSelectAll}
                  paginatedData={paginatedData}
                  handleSelectRow={handleSelectRow}
                  onPatientCCIdChange={handlePatientCCIdChange}
                  onStatusChange={handleStatusChange}
                  sortConfigs={[]}
                  onSort={handleSort}
                  onLockToggle={isLockEnabled ? handleLockToggle : undefined}
                  lockLoadingLeadId={lockLoadingLeadId}
                  currentUser={currentUser}
                  lockedRecordsCount={lockedRecordsCount}
                  onOpenMergeModal={handleOpenMergeModal}
                  hideLockColumn={hideTabSpecificFeatures || !isLockEnabled}
                  hideDedupeSelectColumn={hideTabSpecificFeatures}
                  hideSendToCCButton={hideTabSpecificFeatures}
                  isProcessedTab={isProcessedTab}
                  expandedConfig={expandedConfig}
                  loading={loading}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  defaultSortField={isProcessedTab ? 'processedDateTime' : 'registrationDateTime'}
                  onRemoveRecord={handleRemoveRecord}
                  confirmationModalMessages={props.fields}
                  expandRowId={expandRowIdAfterLock}
                  onExpandedRowIdApplied={() => setExpandRowIdAfterLock(null)}
                  onSendToCCLockValidationError={(message) =>
                    setLockAlert({ severity: 'error', message })
                  }
                  isLockingEnabled={isLockEnabled}
                  currentTabKey={currentTabKey}
                  PatientProfileConfig={props.fields.PatientProfile?.fields}
                />

                <PaginationControls
                  totalPages={totalPages}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalItems={totalRecords}
                  loading={loading}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                />

                {isSuperAdmin && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => router.push('/lead-locks-admin')}
                      sx={{
                        borderColor: '#255E5E',
                        color: '#255E5E',
                        '&:hover': {
                          borderColor: '#1a4a4a',
                          backgroundColor: 'rgba(37, 94, 94, 0.04)',
                        },
                      }}
                    >
                      Lead Locks Admin
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>

          {/* Merge Comparison Modal */}
          {selectedMergeRow && mergeModalOpen && (
            <MergeComparisonModal
              open={mergeModalOpen}
              onClose={handleCloseMergeModal}
              sourceRecord={selectedMergeRow}
              targetRecord={selectedMergeTarget}
              onMerge={performMerge}
              confirmationModalMessages={props.fields}
              isSending={isSending}
            />
          )}
          <ConfirmationDialog
            open={showDialog}
            onClose={handleCloseDialog}
            onConfirm={dialogMode === 'retry' ? handleRetryMerge : handleCloseDialog}
            onCancel={handleCancelAction}
            mode={dialogMode}
            message={
              dialogMode === 'confirm'
                ? props?.fields?.ConfirmText?.value ||
                  'Are you sure you want to send this record to CC?'
                : resultMessage
            }
            title="Confirm Send to CC"
            confirmButtonText={
              dialogMode === 'retry' ? 'Retry' : props?.fields?.ConfirmButtonText?.value || 'Yes'
            }
            cancelButtonText={props?.fields?.ConfirmDismissButtonText?.value || 'Cancel'}
          />
        </StyledPaper>
      </StyledContainer>

      {/* Floating lock alert - visible regardless of scroll position */}
      {lockAlert && (
        <Box
          sx={{
            position: 'fixed',
            top: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: 480,
            px: 2,
            zIndex: 1300,
          }}
        >
          <Alert
            severity={lockAlert.severity}
            onClose={() => setLockAlert(null)}
            sx={{
              boxShadow: 3,
              borderRadius: 1,
            }}
          >
            {lockAlert.message}
          </Alert>
        </Box>
      )}
      <ToastNotification />
    </ThemeProvider>
  );
};

export default LeadManagement;
